import { AggregateSource } from 'hooks/useGrouping';
import React, { useState } from 'react';
import { getSignColor } from '../yearly-report/YearlyReport';
import { getAggregateSourceName } from 'components/features/utils/converter';
import {
  addSignCommaFormat,
  addSignCommaFormatPercentage,
  CommaFormattedString,
  getSign,
  Sign,
  SignAddedCommaFormattedPercentageString,
  SignAddedCommaFormattedString,
} from '../utils/formatter';
import Icon from 'components/commons/icon/Icon';
import { AboutExchangeRateModalButton } from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import * as consts from 'config/consts';
import {
  ProductTypes,
  productTypesToColor,
  productTypesToString,
} from 'components/features/utils/productTypes';

const SORTED = Symbol('SORTED');
type Sorted<T> = { [SORTED]: never } & T[];

export const sortAccordionData = (
  data: [
    Omit<BankAssetChangeAccordionProps, 'periodVerbose'>,
    {
      DiffTotalAssetAmount: number;
    }
  ][]
): Sorted<Omit<BankAssetChangeAccordionProps, 'periodVerbose'>> => {
  return [...data]
    .sort((a, b) => {
      const getSign = (v: number) => (v > 0 ? 'plus' : 'minus');
      const signA = getSign(a[1].DiffTotalAssetAmount);
      const signB = getSign(b[1].DiffTotalAssetAmount);
      if (signA !== signB) {
        return b[1].DiffTotalAssetAmount - a[1].DiffTotalAssetAmount;
      }
      if (signA === 'plus') {
        return b[1].DiffTotalAssetAmount - a[1].DiffTotalAssetAmount;
      } else {
        return a[1].DiffTotalAssetAmount - b[1].DiffTotalAssetAmount;
      }
    })
    .map(datum => datum[0]) as Sorted<
    Omit<BankAssetChangeAccordionProps, 'periodVerbose'>
  >;
};

export type AssetChangePerAccountProps = {
  accordionData: Sorted<Omit<BankAssetChangeAccordionProps, 'periodVerbose'>>;
  periodVerbose: string;
};

export const ASSET_CHANGE_ITEM_TYPE = {
  Stock: 1,
  Margin: 2,
  ForeignStock: 3,
  Investment: 4,
  RoboAdvisor: 5,
  Cash: 6,
  Other: 7,
  UnlistedStock: 8,
  EtfEtn: 9,
  ForeignEtfEtn: 10,
  Reit: 11,
  ForeignReit: 12,
  Bond: 13,
  ForeignBond: 14,
  Eb: 15,
  ForeignEb: 16,
  RealEstate: 17,
} as const;
type AssetChangeItemType = typeof ASSET_CHANGE_ITEM_TYPE[keyof typeof ASSET_CHANGE_ITEM_TYPE];

const getProductType = (type: AssetChangeItemType): ProductTypes => {
  switch (type) {
    case ASSET_CHANGE_ITEM_TYPE.Stock:
      return consts.productTypes.国内株式;
    case ASSET_CHANGE_ITEM_TYPE.Margin:
      return consts.productTypes.国内株式;
    case ASSET_CHANGE_ITEM_TYPE.ForeignStock:
      return consts.productTypes.外国株式;
    case ASSET_CHANGE_ITEM_TYPE.Investment:
      return consts.productTypes.投資信託;
    case ASSET_CHANGE_ITEM_TYPE.RoboAdvisor:
      return consts.productTypes.ロボアド;
    case ASSET_CHANGE_ITEM_TYPE.Cash:
      return consts.productTypes.預金_MRF;
    case ASSET_CHANGE_ITEM_TYPE.Other:
      return consts.productTypes.その他;
    case ASSET_CHANGE_ITEM_TYPE.UnlistedStock:
      return consts.productTypes.未上場株式;
    case ASSET_CHANGE_ITEM_TYPE.EtfEtn:
      return consts.productTypes['国内ETF・ETN'];
    case ASSET_CHANGE_ITEM_TYPE.ForeignEtfEtn:
      return consts.productTypes['外国ETF・ETN'];
    case ASSET_CHANGE_ITEM_TYPE.Reit:
      return consts.productTypes.国内REIT;
    case ASSET_CHANGE_ITEM_TYPE.ForeignReit:
      return consts.productTypes.外国REIT;
    case ASSET_CHANGE_ITEM_TYPE.Bond:
      return consts.productTypes.国内債券;
    case ASSET_CHANGE_ITEM_TYPE.ForeignBond:
      return consts.productTypes.外国債券;
    case ASSET_CHANGE_ITEM_TYPE.Eb:
      return consts.productTypes.国内EB;
    case ASSET_CHANGE_ITEM_TYPE.ForeignEb:
      return consts.productTypes.外国EB;
    case ASSET_CHANGE_ITEM_TYPE.RealEstate:
      return consts.productTypes.不動産;
    default:
      return consts.productTypes.その他;
  }
};

type BankAssetChangeAccordionItemProps<T extends AssetChangeItemType> =
  | {
      type: AssetChangeItemType;
      comparisonSign: Sign;
      comparison: SignAddedCommaFormattedString;
      comparisonPercentage: SignAddedCommaFormattedPercentageString;
    }
  | {
      isZero: true;
    };

type ItemDataProps = [
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Stock>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Margin>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.ForeignStock>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Investment>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.RoboAdvisor>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Cash>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Other>,
  BankAssetChangeAccordionItemProps<
    typeof ASSET_CHANGE_ITEM_TYPE.UnlistedStock
  >,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.EtfEtn>,
  BankAssetChangeAccordionItemProps<
    typeof ASSET_CHANGE_ITEM_TYPE.ForeignEtfEtn
  >,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Reit>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.ForeignReit>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Bond>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.ForeignBond>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.Eb>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.ForeignEb>,
  BankAssetChangeAccordionItemProps<typeof ASSET_CHANGE_ITEM_TYPE.RealEstate>
];

type BankAssetChangeAccordionProps = {
  aggregateSource: AggregateSource;
  periodVerbose: string;
  bankName: string;
  comparisonSign: Sign;
  comparison: SignAddedCommaFormattedString;
  assetAmount: CommaFormattedString;
  comparisonPercentage: SignAddedCommaFormattedPercentageString;
  isEntirePeriodData: boolean;
  itemsData: ItemDataProps;
};

const BankAssetChangeAccordionItem = <T extends AssetChangeItemType>(
  props: BankAssetChangeAccordionItemProps<T>
): React.ReactElement => {
  if ('isZero' in props) return <></>;
  const { type, comparisonSign, comparison, comparisonPercentage } = props;

  const productType = getProductType(type);
  const str = productTypesToString(
    productType,
    type === ASSET_CHANGE_ITEM_TYPE.Margin
      ? consts.CashMargin.信用
      : consts.CashMargin.現物
  );
  const color = productTypesToColor(
    productType,
    type === ASSET_CHANGE_ITEM_TYPE.Margin
      ? consts.CashMargin.信用
      : consts.CashMargin.現物
  );
  return (
    <>
      <li className="flex items-start pt-3 pl-3">
        <div
          className={`mt-1 ${color} rounded-full`}
          style={{ width: '14px', height: '14px' }}></div>
        <div
          className={`flex-1 pb-2 pr-4 pl-2 ml-0 border-b border-bluegray-200 last:border-b-0`}
          style={{ minWidth: 0 }}>
          <div className="h-6 flex items-center">
            <div
              className="text-sm font-bold text-bluegray-700"
              style={{ lineHeight: '17px' }}>
              {str}
            </div>
            {(props.type === ASSET_CHANGE_ITEM_TYPE.ForeignStock ||
              props.type === ASSET_CHANGE_ITEM_TYPE.Cash) && (
              <AboutExchangeRateModalButton
                isShowMessage={false}
                className="ml-auto"></AboutExchangeRateModalButton>
            )}
          </div>
          <div className="h-6 flex items-center mt-2">
            <div
              className={`flex items-center justify-end ml-auto text-sm font-bold ${getSignColor(
                comparisonSign
              )}`}
              style={{ minWidth: '45%' }}>
              {comparison}円 ({comparisonPercentage}%)
            </div>
          </div>
        </div>
      </li>
    </>
  );
};

const NoComparison: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="p-6 text-sm text-center text-bluegray-500 bg-bluegray-50 border border-bluegray-200 rounded-7">
        資産の変動はありません
      </div>
    </>
  );
};

const BankAssetChangeAccordion: React.FC<BankAssetChangeAccordionProps> = ({
  aggregateSource,
  periodVerbose,
  bankName,
  comparisonSign,
  comparison,
  assetAmount,
  comparisonPercentage,
  isEntirePeriodData,
  itemsData,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleAccordion = (): void => {
    setIsOpen(prev => !prev);
  };
  const isItemsDataEmpty = itemsData.every(d => 'isZero' in d && d.isZero);

  return (
    <>
      <div className="mb-4 border border-bluegray-200 rounded-7">
        <div className="p-4 cursor-pointer" onClick={toggleAccordion}>
          <div className="flex items-center justify-between text-xs text-bluegray-500">
            <div className="panel-label">
              {getAggregateSourceName(aggregateSource)}
            </div>
            <div className="panel-label mr-8">評価額前{periodVerbose}比</div>
          </div>
          <div className="flex items-center justify-between mt-0.5 font-bold">
            <div className="text-base">{bankName}</div>
            <div
              className={`${getSignColor(
                comparisonSign
              )} flex items-center justify-end flex-1 text-lg`}>
              {comparison}
              <div className="mr-4 ml-1 text-base">円</div>
            </div>
            <Icon
              className="text-2xl transition duration-300"
              name={
                isOpen
                  ? 'chevron-down transform rotate-180'
                  : 'chevron-down transform rotate-0'
              }
            />
          </div>
          <div className="flex items-center justify-between mt-0.5 text-xs text-bluegray-500">
            <div className="flex items-center">
              <div className="mr-2">資産額</div>
              <div className="text-bluegray-700">{assetAmount}円</div>
            </div>
            <div className={`panel-value mr-8 ${getSignColor(comparisonSign)}`}>
              ({comparisonPercentage}%)
            </div>
          </div>
          {!isEntirePeriodData && (
            <div className="form-error-note right-display">
              <div className="icon-alert"></div>
              <div className="info-label">登録日からの変動額です</div>
            </div>
          )}
        </div>
        <div
          className={`transition-all duration-300 ${
            isOpen
              ? 'ui-effect-fade-in-300 visible'
              : 'ui-effect-fade-out-300 invisible'
          }`}
          style={{
            maxHeight: `${isOpen ? itemsData.length * 77 + 23 : 0}px`,
          }}>
          {isItemsDataEmpty ? (
            <NoComparison />
          ) : (
            <>
              <div className="h-6 flex items-center py-0 px-4 text-xs font-bold text-bluegray-500 bg-bluegray-50 border-t border-bluegray-200">
                内訳
              </div>
              <ul className="">
                {itemsData.map((p, i) => {
                  return <BankAssetChangeAccordionItem {...p} key={i} />;
                })}
              </ul>{' '}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const NoAccount: React.FC<Record<string, unknown>> = () => {
  return (
    <>
      <div className="p-6 text-sm text-center text-bluegray-500 bg-bluegray-50 border border-bluegray-200 rounded-7">
        現在連携中の口座はありません
      </div>
    </>
  );
};

const useToggle = (
  b: boolean
): [
  boolean,
  { turnOff: () => void; turnOn: () => void; toggle: () => void }
] => {
  const [isOn, setIsOn] = useState(b);
  const turnOff = () => setIsOn(false);
  const turnOn = () => setIsOn(true);
  const toggle = () => setIsOn(prev => !prev);
  return [isOn, { turnOff, turnOn, toggle }];
};

type Either<L, R> =
  | {
      tag: 'zoro';
      value: L;
    }
  | {
      tag: 'notZero';
      value: R;
    };
const convertZero = <L, R>(value: L): Either<L, R> => {
  return {
    tag: 'zoro',
    value,
  };
};
const convertNotZero = <L, R>(value: R): Either<L, R> => {
  return {
    tag: 'notZero',
    value,
  };
};

export const AssetChangePerAccount: React.FC<AssetChangePerAccountProps> = ({
  accordionData,
  periodVerbose,
}) => {
  const isEmpty = accordionData.length === 0;
  const [isShowNoChanges, showNoChanges] = useToggle(false);
  const separatedAccordionData = accordionData
    .map(
      x =>
        (x.comparison !== '0' ? convertNotZero(x) : convertZero(x)) as Either<
          typeof accordionData[number],
          typeof accordionData[number]
        >
    )
    .reduce(
      (acc, v) => {
        return {
          ...acc,
          zeros: [acc.zeros, v.tag === 'zoro' ? [v.value] : []].flat(),
          notZeros: [acc.notZeros, v.tag === 'notZero' ? [v.value] : []].flat(),
        };
      },
      {
        zeros: [] as typeof accordionData[number][],
        notZeros: [] as typeof accordionData[number][],
      }
    );
  const toggleButtonMessage = '変動がない資産も全て表示';

  return (
    <>
      <div className="p-4 overflow-hidden">
        <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
          口座別資産変動（変動額順）
        </div>
        {isEmpty && <NoAccount />}
        {separatedAccordionData.notZeros.map((p, i) => {
          return (
            <BankAssetChangeAccordion
              key={i}
              {...p}
              periodVerbose={periodVerbose}
            />
          );
        })}
        {!isEmpty && (
          <>
            {isShowNoChanges ? (
              separatedAccordionData.zeros.map((p, i) => {
                return (
                  <BankAssetChangeAccordion
                    key={i}
                    {...p}
                    periodVerbose={periodVerbose}
                  />
                );
              })
            ) : (
              <div
                className="flex items-center justify-center text-base text-bluegray-600 font-bold leading-6 border border-bluegray-300 rounded-lg h-10 mt-6 bg-gray-100 mx-8 mb-6 cursor-pointer"
                onClick={showNoChanges.toggle}>
                {toggleButtonMessage}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const NOT_ZERO = Symbol('NOT_ZERO');
type NotZero = { [NOT_ZERO]: never } & number;
const toNotZero = (num: number | undefined | null): 0 | NotZero => {
  if (!num) return 0;
  return num as NotZero;
};
const isNotZero = (num: 0 | NotZero): num is NotZero => {
  return num !== 0;
};

export const toItemsData = ({
  DiffCashAmount,
  DiffCashRate,
  DiffMarginProfitOrLoss,
  DiffMarginProfitOrLossRate,
  DiffUSStockAmount,
  DiffUSStockRate,
  DiffInvestmentTrustAmount,
  DiffInvestmentTrustRate,
  DiffRoboAdvisorAmount,
  DiffRoboAdvisorRate,
  DiffOtherAmount,
  DiffOtherRate,
  DiffStockAmount,
  DiffStockRate,
  DiffUnlistedStockAmount,
  DiffUnlistedStockRate,
  DiffEtfEtnAmount,
  DiffEtfEtnRate,
  DiffForeignEtfEtnAmount,
  DiffForeignEtfEtnRate,
  DiffReitAmount,
  DiffReitRate,
  DiffForeignReitAmount,
  DiffForeignReitRate,
  DiffBondAmount,
  DiffBondRate,
  DiffForeignBondAmount,
  DiffForeignBondRate,
  DiffEbAmount,
  DiffEbRate,
  DiffForeignEbAmount,
  DiffForeignEbRate,
  DiffRealEstateAmount,
  DiffRealEstateRate,
}: {
  DiffStockAmount: number;
  DiffStockRate: number;
  DiffMarginProfitOrLoss: number;
  DiffMarginProfitOrLossRate: number;
  DiffUSStockAmount: number;
  DiffUSStockRate: number;
  DiffInvestmentTrustAmount: number;
  DiffInvestmentTrustRate: number;
  DiffRoboAdvisorAmount: number;
  DiffRoboAdvisorRate: number;
  DiffCashAmount: number;
  DiffCashRate: number;
  DiffOtherAmount: number;
  DiffOtherRate: number;
  DiffUnlistedStockAmount: number;
  DiffUnlistedStockRate: number;
  DiffEtfEtnAmount: number;
  DiffEtfEtnRate: number;
  DiffForeignEtfEtnAmount: number;
  DiffForeignEtfEtnRate: number;
  DiffReitAmount: number;
  DiffReitRate: number;
  DiffForeignReitAmount: number;
  DiffForeignReitRate: number;
  DiffBondAmount: number;
  DiffBondRate: number;
  DiffForeignBondAmount: number;
  DiffForeignBondRate: number;
  DiffEbAmount: number;
  DiffEbRate: number;
  DiffForeignEbAmount: number;
  DiffForeignEbRate: number;
  DiffRealEstateAmount: number;
  DiffRealEstateRate: number;
}): ItemDataProps => {
  const toZeroProps = (_: 0): { isZero: true } => ({ isZero: true });

  const toItemData = <T extends AssetChangeItemType>(
    DiffNotZero: NotZero,
    DiffRate: number,
    type: AssetChangeItemType
  ): BankAssetChangeAccordionItemProps<T> => {
    const ret = {
      type,
      comparison: addSignCommaFormat(DiffNotZero),
      comparisonPercentage: addSignCommaFormatPercentage(DiffRate, {
        numberOfDecimals: 2,
      }),
      comparisonSign: getSign(DiffNotZero),
    };
    return ret;
  };

  const toProps = <T extends AssetChangeItemType>(
    Diff_: number,
    DiffRate: number,
    type: AssetChangeItemType
  ): BankAssetChangeAccordionItemProps<T> => {
    const Diff = toNotZero(Diff_);
    if (isNotZero(Diff)) return toItemData(Diff, DiffRate, type);
    return toZeroProps(Diff);
  };

  const ret: ItemDataProps = [
    toProps(DiffStockAmount, DiffStockRate, ASSET_CHANGE_ITEM_TYPE.Stock),
    toProps(
      DiffUnlistedStockAmount,
      DiffUnlistedStockRate,
      ASSET_CHANGE_ITEM_TYPE.UnlistedStock
    ),
    toProps(
      DiffInvestmentTrustAmount,
      DiffInvestmentTrustRate,
      ASSET_CHANGE_ITEM_TYPE.Investment
    ),
    toProps(
      DiffUSStockAmount,
      DiffUSStockRate,
      ASSET_CHANGE_ITEM_TYPE.ForeignStock
    ),
    toProps(DiffEtfEtnAmount, DiffEtfEtnRate, ASSET_CHANGE_ITEM_TYPE.EtfEtn),
    toProps(
      DiffForeignEtfEtnAmount,
      DiffForeignEtfEtnRate,
      ASSET_CHANGE_ITEM_TYPE.ForeignEtfEtn
    ),
    toProps(DiffReitAmount, DiffReitRate, ASSET_CHANGE_ITEM_TYPE.Reit),
    toProps(
      DiffForeignReitAmount,
      DiffForeignReitRate,
      ASSET_CHANGE_ITEM_TYPE.ForeignReit
    ),
    toProps(DiffBondAmount, DiffBondRate, ASSET_CHANGE_ITEM_TYPE.Bond),
    toProps(
      DiffForeignBondAmount,
      DiffForeignBondRate,
      ASSET_CHANGE_ITEM_TYPE.ForeignBond
    ),
    toProps(DiffEbAmount, DiffEbRate, ASSET_CHANGE_ITEM_TYPE.Eb),
    toProps(
      DiffForeignEbAmount,
      DiffForeignEbRate,
      ASSET_CHANGE_ITEM_TYPE.ForeignEb
    ),
    toProps(
      DiffRealEstateAmount,
      DiffRealEstateRate,
      ASSET_CHANGE_ITEM_TYPE.RealEstate
    ),
    toProps(
      DiffRoboAdvisorAmount,
      DiffRoboAdvisorRate,
      ASSET_CHANGE_ITEM_TYPE.RoboAdvisor
    ),
    toProps(
      DiffMarginProfitOrLoss,
      DiffMarginProfitOrLossRate,
      ASSET_CHANGE_ITEM_TYPE.Margin
    ),
    toProps(DiffCashAmount, DiffCashRate, ASSET_CHANGE_ITEM_TYPE.Cash),
    toProps(DiffOtherAmount, DiffOtherRate, ASSET_CHANGE_ITEM_TYPE.Other),
  ];

  return ret;
};
